import type { FC } from "react";
import { useCallback, useEffect, useRef, useState } from "react";

import { useBlocNext } from "@blac/react";
import { css, keyframes } from "@emotion/css";
import styled from "@emotion/styled";
import * as pdfjsLib from "pdfjs-dist";
import pdfjsWorker from "pdfjs-dist/build/pdf.worker.min.mjs?url";
import translate from "src/lib/translate";

import { LoadingSpinner } from "../BlockingLoadingOverlay/BlockingLoadingOverlay";
import InDialog from "../InDialog/InDialog";
import PDFViewerBloc from "./PDFViewerBloc";

pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorker;

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const slideUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const pdfDialogStyles = css`
  --dialog-background: #f8f9fa;
  --toolbar-background: #fff;

  ion-toolbar {
    --border-width: 0;
    box-shadow: 0 1px 0 rgba(0, 0, 0, 0.06);
  }

  ion-content {
    &::part(scroll) {
      overflow: hidden;
      padding: 0;
    }
  }
`;

const PDFContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  background: #f8f9fa;
  overflow: hidden;
`;

const CanvasContainer = styled.div`
  position: relative;
  flex: 1;
  overflow: auto;
  background: linear-gradient(180deg, #f0f2f5 0%, #e8eaed 100%);
  touch-action: pan-x pan-y pinch-zoom;
  -webkit-overflow-scrolling: touch;
  padding: 1.5rem 1rem;

  @media (min-width: 768px) {
    padding: 2rem;
  }

  &[data-loading="true"] {
    pointer-events: none;

    canvas {
      opacity: 0;
      transform: scale(0.98);
    }
  }

  canvas {
    position: relative;
    display: block;
    margin: 0 auto;
    background: #fff;
    border-radius: 4px;
    box-shadow:
      0 2px 8px rgba(0, 0, 0, 0.08),
      0 8px 24px rgba(0, 0, 0, 0.06);
    transition:
      opacity 0.25s ease-out,
      transform 0.25s ease-out;
    animation: ${fadeIn} 0.3s ease-out;
  }
`;

const LoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
  z-index: 10;
  opacity: 0;
  transition: opacity 0.2s ease-out;
  background: rgba(248, 249, 250, 0.7);
  backdrop-filter: blur(2px);

  &[data-loading="true"] {
    opacity: 1;
  }
`;

const FloatingPageBadge = styled.div`
  position: absolute;
  top: 1rem;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(0, 0, 0, 0.7);
  color: #fff;
  padding: 0.375rem 0.75rem;
  border-radius: 100px;
  font-size: 0.8125rem;
  font-weight: 500;
  letter-spacing: 0.02em;
  z-index: 5;
  backdrop-filter: blur(8px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  animation: ${slideUp} 0.3s ease-out;
  transition: opacity 0.2s ease-out;
  pointer-events: none;

  &[data-hidden="true"] {
    opacity: 0;
  }
`;

const ControlBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.625rem 1rem;
  padding-bottom: calc(0.625rem + env(safe-area-inset-bottom, 0px));
  background: #fff;
  border-top: 1px solid rgba(0, 0, 0, 0.06);
  gap: 0.5rem;

  @media (min-width: 768px) {
    padding: 0.75rem 1.5rem;
    padding-bottom: calc(0.75rem + env(safe-area-inset-bottom, 0px));
  }
`;

const NavSection = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;

  &[data-hidden="true"] {
    visibility: hidden;
    pointer-events: none;
  }
`;

const ZoomSection = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
`;

const IconButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border: none;
  border-radius: 12px;
  background: transparent;
  color: var(--color-charcoal, #2d3436);
  cursor: pointer;
  transition: all 0.15s ease-out;
  -webkit-tap-highlight-color: transparent;

  @media (hover: hover) {
    &:hover:not(:disabled) {
      background: rgba(0, 0, 0, 0.05);
    }
  }

  &:active:not(:disabled) {
    background: rgba(0, 0, 0, 0.08);
    transform: scale(0.96);
  }

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }

  svg {
    width: 22px;
    height: 22px;
  }
`;

const ZoomDisplay = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 52px;
  height: 36px;
  padding: 0 0.5rem;
  border: none;
  border-radius: 8px;
  background: rgba(0, 0, 0, 0.04);
  color: var(--color-charcoal, #2d3436);
  font-size: 0.8125rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease-out;
  -webkit-tap-highlight-color: transparent;

  @media (hover: hover) {
    &:hover:not(:disabled) {
      background: rgba(0, 0, 0, 0.08);
    }
  }

  &:active:not(:disabled) {
    transform: scale(0.97);
  }

  &:disabled {
    cursor: default;
  }
`;

const ErrorToast = styled.div`
  position: absolute;
  bottom: 5rem;
  left: 50%;
  transform: translateX(-50%);
  max-width: calc(100% - 2rem);
  padding: 0.875rem 1.25rem;
  background: var(--color-afternoon-yellow, #fff3cd);
  color: var(--color-charcoal, #2d3436);
  border-radius: 12px;
  box-shadow:
    0 4px 12px rgba(0, 0, 0, 0.1),
    0 1px 3px rgba(0, 0, 0, 0.08);
  font-size: 0.875rem;
  font-weight: 500;
  text-align: center;
  z-index: 15;
  animation: ${slideUp} 0.3s ease-out;
`;

const ChevronLeftIcon: FC = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="15 18 9 12 15 6" />
  </svg>
);

const ChevronRightIcon: FC = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="9 18 15 12 9 6" />
  </svg>
);

const MinusIcon: FC = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

const PlusIcon: FC = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="12" y1="5" x2="12" y2="19" />
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

const PDFViewer: FC = () => {
  const [
    state,
    {
      openDocument,
      setCanvasEl,
      setContainerEl,
      handleNextPage,
      handlePrevPage,
      close,
      renderPage,
      zoomIn,
      zoomOut,
      resetZoom,
      setZoom
    }
  ] = useBlocNext(PDFViewerBloc);

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const initialPinchDistance = useRef<number | null>(null);
  const initialZoom = useRef<number>(1);
  const [showPageBadge, setShowPageBadge] = useState(true);

  useEffect(() => {
    if (canvasRef.current) {
      setCanvasEl(canvasRef.current);
    }
  }, [canvasRef.current, setCanvasEl]);

  useEffect(() => {
    if (containerRef.current) {
      setContainerEl(containerRef.current);
    }
  }, [containerRef.current, setContainerEl]);

  useEffect(() => {
    if (state.blobUrl) {
      const loadingTask = pdfjsLib.getDocument(state.blobUrl);
      void openDocument(loadingTask);
    }
  }, [state.blobUrl, openDocument]);

  useEffect(() => {
    if (!state.isOpen || state.isRendering) return;

    if (containerRef.current) {
      containerRef.current.scrollTop = 0;
      containerRef.current.scrollLeft = 0;
    }

    void renderPage();
  }, [state.currentPage]);

  useEffect(() => {
    if (state.numPages > 1) {
      setShowPageBadge(true);
      const timer = setTimeout(() => setShowPageBadge(false), 2000);
      return () => clearTimeout(timer);
    }
  }, [state.currentPage, state.numPages]);

  const getTouchDistance = (touches: React.TouchList): number => {
    const dx = touches[0].clientX - touches[1].clientX;
    const dy = touches[0].clientY - touches[1].clientY;
    return Math.sqrt(dx * dx + dy * dy);
  };

  const handleTouchStart = (e: React.TouchEvent): void => {
    if (e.touches.length === 2) {
      const distance = getTouchDistance(e.touches);
      initialPinchDistance.current = distance;
      initialZoom.current = state.zoom;
    }
  };

  const handleTouchMove = (e: React.TouchEvent): void => {
    if (e.touches.length === 2 && initialPinchDistance.current) {
      e.preventDefault();
      const distance = getTouchDistance(e.touches);
      const scale = distance / initialPinchDistance.current;
      const newZoom = initialZoom.current * scale;
      setZoom(newZoom);
    }
  };

  const handleTouchEnd = (): void => {
    initialPinchDistance.current = null;
  };

  const handleWheel = (e: React.WheelEvent): void => {
    if (e.ctrlKey || e.metaKey) {
      e.preventDefault();
      const delta = e.deltaY > 0 ? -0.1 : 0.1;
      setZoom(state.zoom + delta);
    }
  };

  const handleSwipe = useCallback(
    (direction: "left" | "right") => {
      if (state.isRendering || state.zoom > 1) return;

      if (direction === "left" && state.currentPage < state.numPages) {
        handleNextPage();
      } else if (direction === "right" && state.currentPage > 1) {
        handlePrevPage();
      }
    },
    [
      state.isRendering,
      state.zoom,
      state.currentPage,
      state.numPages,
      handleNextPage,
      handlePrevPage
    ]
  );

  const swipeStartX = useRef<number | null>(null);

  const handleSwipeStart = (e: React.TouchEvent): void => {
    if (e.touches.length === 1 && state.zoom <= 1) {
      swipeStartX.current = e.touches[0].clientX;
    }
  };

  const handleSwipeEnd = (e: React.TouchEvent): void => {
    if (swipeStartX.current !== null && e.changedTouches.length === 1) {
      const endX = e.changedTouches[0].clientX;
      const diff = swipeStartX.current - endX;
      const threshold = 80;

      if (Math.abs(diff) > threshold) {
        handleSwipe(diff > 0 ? "left" : "right");
      }
      swipeStartX.current = null;
    }
  };

  const handleZoomDisplayClick = (): void => {
    if (state.zoom !== 1.0 && !state.isRendering) {
      resetZoom();
    }
  };

  const isMultiPage = state.numPages > 1;
  const zoomPercentage = Math.round(state.zoom * 100);

  return (
    <InDialog
      isOpen={state.isOpen}
      isFullScreen={true}
      onClose={close}
      backdropDismiss={false}
      title={state.filename ?? ""}
      width="calc(100vw - 2rem)"
      height="calc(100vh - 2rem)"
      noPadding
      popup
      simple
      className={pdfDialogStyles}
    >
      <PDFContainer>
        <CanvasContainer
          ref={containerRef}
          data-loading={state.loading || state.isRendering}
          data-error={!!state.error}
          onTouchStart={(e) => {
            handleTouchStart(e);
            handleSwipeStart(e);
          }}
          onTouchMove={handleTouchMove}
          onTouchEnd={(e) => {
            handleTouchEnd();
            handleSwipeEnd(e);
          }}
          onWheel={handleWheel}
        >
          <canvas ref={canvasRef} />
        </CanvasContainer>

        <LoadingOverlay data-loading={state.loading || state.isRendering}>
          <LoadingSpinner />
        </LoadingOverlay>

        {isMultiPage && (
          <FloatingPageBadge data-hidden={!showPageBadge && !state.isRendering}>
            {state.currentPage} of {state.numPages}
          </FloatingPageBadge>
        )}

        {state.error && <ErrorToast>{translate(state.error)}</ErrorToast>}

        <ControlBar>
          <NavSection data-hidden={!isMultiPage}>
            <IconButton
              onClick={handlePrevPage}
              disabled={state.currentPage <= 1 || state.isRendering}
              aria-label={translate("previous")}
            >
              <ChevronLeftIcon />
            </IconButton>
            <IconButton
              onClick={handleNextPage}
              disabled={
                state.currentPage >= state.numPages || state.isRendering
              }
              aria-label={translate("next")}
            >
              <ChevronRightIcon />
            </IconButton>
          </NavSection>

          <ZoomSection>
            <IconButton
              onClick={zoomOut}
              disabled={state.zoom <= state.minZoom || state.isRendering}
              aria-label="Zoom out"
            >
              <MinusIcon />
            </IconButton>
            <ZoomDisplay
              onClick={handleZoomDisplayClick}
              disabled={state.zoom === 1.0 || state.isRendering}
              aria-label={state.zoom !== 1.0 ? "Reset zoom" : undefined}
            >
              {zoomPercentage}%
            </ZoomDisplay>
            <IconButton
              onClick={zoomIn}
              disabled={state.zoom >= state.maxZoom || state.isRendering}
              aria-label="Zoom in"
            >
              <PlusIcon />
            </IconButton>
          </ZoomSection>
        </ControlBar>
      </PDFContainer>
    </InDialog>
  );
};

export default PDFViewer;
